import type {
  PageContextEntities,
  PageContextTimeRange,
  PageContextV1,
  PageContextView,
  PageType,
} from './types'
import {PAGE_TYPES} from './types'

// The contract's limits (spec "Schema (v1)"). The backend enforces the same ones
// leniently; trimming here keeps a large selection from being dropped whole.
export const PAGE_CONTEXT_MAX_BYTES = 4096
export const PAGE_CONTEXT_MAX_IDS = 50
export const PAGE_CONTEXT_MAX_STRING = 80

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const SINGLE_ID_KEYS = [
  'line_id',
  'equipment_id',
  'dashboard_id',
  'incident_id',
  'program_id',
] as const
const LIST_ID_KEYS = ['batch_ids', 'signal_ids'] as const
const VIEW_KEYS = ['tab', 'mode', 'settings_section'] as const

const isUuid = (value: unknown): value is string => typeof value === 'string' && UUID_RE.test(value)

const shortString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0
    ? value.slice(0, PAGE_CONTEXT_MAX_STRING)
    : undefined

const isoString = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.length > PAGE_CONTEXT_MAX_STRING) {
    return undefined
  }
  return Number.isNaN(Date.parse(value)) ? undefined : value
}

const sanitizeEntities = (
  raw: PageContextEntities | undefined,
): PageContextEntities | undefined => {
  if (!raw) return undefined
  const out: PageContextEntities = {}
  for (const key of SINGLE_ID_KEYS) {
    if (isUuid(raw[key])) out[key] = raw[key]
  }
  for (const key of LIST_ID_KEYS) {
    const list = raw[key]
    if (!Array.isArray(list)) continue
    const ids = Array.from(new Set(list.filter(isUuid))).slice(0, PAGE_CONTEXT_MAX_IDS)
    if (ids.length) out[key] = ids
  }
  return Object.keys(out).length ? out : undefined
}

const sanitizeTimeRange = (
  raw: PageContextTimeRange | undefined,
): PageContextTimeRange | undefined => {
  if (!raw) return undefined
  const out: PageContextTimeRange = {}
  const start = isoString(raw.start)
  const end = isoString(raw.end)
  const preset = shortString(raw.preset)
  if (start) out.start = start
  if (end) out.end = end
  if (preset) out.preset = preset
  return Object.keys(out).length ? out : undefined
}

const sanitizeView = (raw: PageContextView | undefined): PageContextView | undefined => {
  if (!raw) return undefined
  const out: PageContextView = {}
  for (const key of VIEW_KEYS) {
    const value = shortString(raw[key])
    if (value) out[key] = value
  }
  return Object.keys(out).length ? out : undefined
}

const byteLength = (value: unknown): number =>
  new TextEncoder().encode(JSON.stringify(value)).length

/**
 * Enforce the v1 limits: known keys only, UUIDs validated, at most 50 ids per
 * list, strings at most 80 chars, at most 4 KB in total. Over the byte limit the
 * longest id list is halved until it fits, so a huge signal selection still sends
 * the page, the window and the first signals.
 */
export const sanitizePageContext = (raw: PageContextV1): PageContextV1 => {
  const page: PageType = (PAGE_TYPES as readonly string[]).includes(raw.page) ? raw.page : 'other'
  const out: PageContextV1 = {
    v: 1,
    page,
    path: (raw.path || '/').slice(0, PAGE_CONTEXT_MAX_STRING),
    captured_at: raw.captured_at,
  }
  const entities = sanitizeEntities(raw.entities)
  const timeRange = sanitizeTimeRange(raw.time_range)
  const view = sanitizeView(raw.view)
  if (entities) out.entities = entities
  if (timeRange) out.time_range = timeRange
  if (view) out.view = view

  while (byteLength(out) > PAGE_CONTEXT_MAX_BYTES && out.entities) {
    const longest = LIST_ID_KEYS.filter((key) => (out.entities?.[key]?.length ?? 0) > 0).sort(
      (a, b) => (out.entities?.[b]?.length ?? 0) - (out.entities?.[a]?.length ?? 0),
    )[0]
    if (!longest) break
    const list = out.entities[longest] ?? []
    const next = list.slice(0, Math.floor(list.length / 2))
    if (next.length) {
      out.entities[longest] = next
    } else {
      delete out.entities[longest]
    }
  }
  return out
}
